import dayjs, { Dayjs } from "dayjs";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const FROM_PARAM = "from";
const TO_PARAM = "to";
const DATE_FORMAT = "YYYY-MM-DD";

export type TradeMatcherDateRange = [Dayjs | null, Dayjs | null];

const parseParam = (value: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value, DATE_FORMAT, true);
  return parsed.isValid() ? parsed : null;
};

/**
 * URL-backed date range for the trade matcher. Defaults to the last 30 days
 * (inclusive: today − 29 → today) when no params are present.
 */
export const useTradeMatcherDateRange = (): [
  TradeMatcherDateRange,
  (next: TradeMatcherDateRange) => void,
] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const fromParam = parseParam(searchParams.get(FROM_PARAM));
  const toParam = parseParam(searchParams.get(TO_PARAM));

  const today = dayjs().startOf("day");
  const from = fromParam ?? today.subtract(29, "day");
  const to = toParam ?? today;

  const setRange = useCallback(
    (next: TradeMatcherDateRange) => {
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          const [nextFrom, nextTo] = next;
          if (nextFrom && nextFrom.isValid()) {
            updated.set(FROM_PARAM, nextFrom.format(DATE_FORMAT));
          } else {
            updated.delete(FROM_PARAM);
          }
          if (nextTo && nextTo.isValid()) {
            updated.set(TO_PARAM, nextTo.format(DATE_FORMAT));
          } else {
            updated.delete(TO_PARAM);
          }
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return [[from, to], setRange];
};
